import { Link } from 'react-router-dom';
import { CheckIcon, GiftIcon, MessageCircleIcon } from 'lucide-react';
import { MediaView } from '../shared/MediaView';
import { ProductImage } from '../../ui/ProductImage';
import { useCatalog } from '../../contexts/CatalogContext';
import { useCartActions } from '../../contexts/StoreContext';
import { usePages, useSite } from '../../contexts/SiteContext';
import { unlockedIn } from '../../data/gifts';
import { shopLabels } from '../../data/shopLabels';
import { founder, salon } from '../../data/content';
import { euro } from '../../core/format/currency';
import { countSoldOut, isAllSoldOut, isSoldOut } from '../../core/rules/stock';
import { fill } from '../../utils/copy';
import { sectionHeading } from '../../ui/sections';
import { Section } from '../shared/Section';
import { btnPrice, btnPriceAmount, btnPriceLabel } from '../../ui/styles';
import type { Routine } from '../../core/types/catalog';
const primary = btnPrice;

/**
 * Het anker van de pagina: het complete protocol, op blush. Bewust geen protocolkaart in een
 * kader (dan stond het verhaal er twee keer), maar één uitgeschreven blok: het beeld blijft staan
 * (computer), rechts het verhaal, de producten in volgorde van gebruik, wat erbij zit en de
 * cadeaus die je met dit protocol vrijspeelt. Eén knop, met de prijs erin.
 */
export function RoutineAnchor({
  routine
}: {
  routine: Routine;
}) {
  const {
    routineTotals
  } = useCatalog();
  const {
    addRoutine
  } = useCartActions();
  const {
    items,
    price,
    months
  } = routineTotals(routine);
  const site = useSite();
  const pages = usePages();
  const gifts = unlockedIn(site.giftTiers, site.policy.freeShippingFrom, price);
  const soldOutCount = countSoldOut(items);
  const allSoldOut = isAllSoldOut(items);
  return <Section tone="blush" labelledBy="anchor-routine" contained={false} data-id="element-8257" data-mp="1j5n1f4gbivmx">
      <div className="mx-auto grid max-w-content gap-10 px-6 lg:grid-cols-12 lg:gap-16 lg:px-10" data-id="element-8258" data-mp="3pwcuqiot2ino">
        <div className="lg:col-span-5" data-id="element-8259" data-mp="3kx8h9fuulyrj">
          <div className="lg:sticky lg:top-28" data-id="element-8260" data-mp="3kyjmzl7utgla">
            <MediaView media={{
            image: routine.image || founder.withProducts,
            alt: ''
          }} sizes="(min-width: 1024px) 38vw, 100vw" className="aspect-[4/3] w-full sm:aspect-[16/10] lg:aspect-[4/5]" data-id="element-8261" data-mp="1ou3u4ceru4nb" />
          </div>
        </div>

        <div className="lg:col-span-7" data-id="element-8262" data-mp="1xs76ai3v9p2g">
          <p className="text-[14px] text-ink" data-id="element-8263" data-mp="22sld5wtqesyw">{fill(pages.routines.anchorEyebrow)}</p>
          <h2 id="anchor-routine" className={`mt-4 ${sectionHeading} text-ink`} data-id="element-8264" data-mp="22qh1utti6rol">
            {routine.name}
          </h2>
          <p className="mt-6 max-w-xl text-[17px] leading-relaxed text-ink" data-id="element-8265" data-mp="2cqhlyhz75btu">{routine.story}</p>
          {routine.forWho && <p className="mt-3 max-w-xl text-[15px] leading-relaxed text-muted" data-id="element-8266" data-mp="2hhhj9oc89vwv">Voor wie: {routine.forWho.toLowerCase()}.</p>}

          {/* De volgorde is de boodschap: zo gebruik je het, van reinigen tot bescherming. */}
          <h3 className="mt-10 text-[14px] text-ink" data-id="element-8267" data-mp="2qjqvcin59xcd">In volgorde van gebruik</h3>
          <ol className="mt-3 divide-y divide-ink/15 border-y border-ink/15" data-id="element-8268" data-mp="2vik7a6ms9gaf">
            {items.map((p, i) => <li key={p.id} className="flex items-center gap-4 py-3" data-id="element-8269" data-mp="35m8othfa6lk9" data-mpi={i}>
                <span aria-hidden="true" className="w-5 shrink-0 type-name text-muted tabular-nums" data-id="element-8270" data-mp="30m35coj605d9">
                  {i + 1}
                </span>
                <ProductImage src={p.image} alt="" width={48} className="h-14 w-12 shrink-0 bg-shell p-1" data-id="element-8271" data-mp="2vpcdl3xzzlc0" />
                <div className="min-w-0 flex-1" data-id="element-8272" data-mp="2ro0xbp10kcgh">
                  <p className="text-[12px] text-ink" data-id="element-8273" data-mp="3oavsbvht72vq">{isSoldOut(p) ? shopLabels.soldOutShort : p.step}</p>
                  <Link to={`/webshop/${p.id}`} className="text-[15px] leading-snug text-ink underline-offset-4 hover:underline" data-id="element-8274" data-mp="3jdvpruk64i9d">
                    {p.name}
                  </Link>
                </div>
                <span className={`text-[14px] tabular-nums text-muted ${isSoldOut(p) ? 'line-through' : ''}`} data-id="element-8275" data-mp="3ajr21k0bzmy2">{euro(p.price)}</span>
              </li>)}
          </ol>

          <div className="mt-8 grid gap-8 sm:grid-cols-2" data-id="element-8276" data-mp="6629bn9rodvq">
            {routine.included.length > 0 && <div data-id="element-8277" data-mp="3msneaddv6hfq">
                <h3 className="text-[14px] text-ink" data-id="element-8278" data-mp="3ms3z34flm75e">Kosteloos inbegrepen</h3>
                <ul className="mt-3 space-y-2" data-id="element-8279" data-mp="3rtt86ej9gaqu">
                  {routine.included.map(inc => <li key={inc} className="flex items-start gap-2.5 text-[15px] leading-snug text-ink" data-id="element-8280" data-mp="3du1y2ovv7ei4">
                      <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-sage" strokeWidth={2} aria-hidden="true" data-id="element-8281" data-mp="3hvdimfzvt9jg" />
                      {inc}
                    </li>)}
                </ul>
              </div>}
            {gifts.length > 0 && <div data-id="element-8282" data-mp="na0h88q10wr">
                <h3 className="text-[14px] text-ink" data-id="element-8283" data-mp="lp7vo5yvs1j">{fill(pages.routines.gifts.heading)}</h3>
                <ul className="mt-3 space-y-2" data-id="element-8284" data-mp="4mr92ajg2e65">
                  {gifts.map(g => <li key={g.ruleId} className="flex items-start gap-2.5 text-[15px] leading-snug text-ink" data-id="element-8285" data-mp="sclkqos11wot">
                      <GiftIcon className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.5} aria-hidden="true" data-id="element-8286" data-mp="x569vczznlm0" />
                      {g.title}
                    </li>)}
                </ul>
              </div>}
          </div>

          <div className="mt-10 max-w-md" data-id="element-8287" data-mp="2rklgd8r2fd63">
            <p className="mb-3 text-[14px] text-muted" data-id="element-8288" data-mp="2rja38ibqct2b">
              Gaat {months} mee · dezelfde prijs als los, zonder giswerk
            </p>
            {allSoldOut ? <a href={salon.whatsapp} target="_blank" rel="noreferrer" className={`${primary} justify-center`} data-id="element-8289" data-mp="1yve99ldjpf98">
                {shopLabels.soldOutShort} · vraag {founder.firstName} om een alternatief
                <span className="sr-only" data-id="element-8290" data-mp="28udknbpp3ozx">{shopLabels.viaWhatsApp}</span>
              </a> : <button type="button" onClick={() => addRoutine(routine.id)} className={primary} data-id="element-8291" data-mp="23tza9n0mg2yq">
                <span className={btnPriceLabel} data-id="element-8292" data-mp="3k8h9k3hfemy4">
                  Protocol in de mand<span className="sr-only" data-id="element-8293" data-mp="3fa5zpsr4scfw">: {routine.name}</span>
                </span>
                <span className={btnPriceAmount} data-id="element-8294" data-mp="3fcil6fw3l4xp">{euro(price)}</span>
              </button>}
            {soldOutCount > 0 && !allSoldOut && <p className="mt-3 text-[13px] leading-relaxed text-muted" data-id="element-8295" data-mp="3rbnsquget77j">{shopLabels.partlySoldOut(soldOutCount)}</p>}
            {/* Twijfel? Eén rustige regel onder de knop, in plaats van een eigen blok "zo werkt het". */}
            {pages.routines.gifts.note && <a href={salon.whatsapp} target="_blank" rel="noreferrer" className="mt-4 flex items-start gap-2 text-[14px] leading-relaxed text-muted underline-offset-4 transition-colors duration-150 ease-soft hover:text-ink hover:underline" data-id="element-8296" data-mp="33mw6sktexl37">
                <MessageCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-ink" strokeWidth={1.5} aria-hidden="true" data-id="element-8297" data-mp="3ic0s4avi85fj" />
                {fill(pages.routines.gifts.note)}
                <span className="sr-only" data-id="element-8298" data-mp="2pvhqilqw3xzu">{shopLabels.viaWhatsApp}</span>
              </a>}
          </div>
        </div>
      </div>
    </Section>;
}